/**
 * embeddings phase — generates vector representations for graph nodes.
 *
 * Enriched composeNodeText includes community summaries and node descriptions.
 * Per-node composed text comparison remains for fine-grained incrementality.
 */
import { existsSync, readFileSync, rmSync, unlinkSync } from "node:fs";
import { join } from "node:path";
import type { Config, GraphData, VectorMeta } from "../../shared/types.js";
import { atomicWriteJson } from "../../shared/atomic-write.js";
import { readJsonSafe } from "../../shared/fs.js";
import { log, errorMessage } from "../../shared/utils.js";
import { composeNodeText } from "../../intelligence/embeddings.js";
import { TfidfEmbeddingEngine } from "../../intelligence/tfidf-embeddings.js";
import type { EmbeddingProvider } from "../../intelligence/llm-provider.js";
import { VectorStore, type VectorRecord } from "../../query/vector-store.js";
import { writeVectorMeta } from "../../query/vector-meta.js";
import { hashObject, readHashFile } from "../cache/utils.js";
import { BasePhase, type PhaseContext, type PhaseResult } from "../engine/phase.js";

class EmbeddingsPhase extends BasePhase {
  readonly id = "embeddings";
  readonly label = "Embeddings";
  readonly dependencies = ["enrich"];
  readonly inputs = ["graph-enriched.json"];

  getExpectedOutputs(config: Config): { files: string[]; dirs: string[] } {
    const files: string[] = [];
    const dirs: string[] = ["vectors"];
    if (!config.embeddings.provider || config.embeddings.provider === "tfidf") {
      files.push("tfidf_idf.json");
    }
    return { files, dirs };
  }

  getRelevantConfig(config: Config): object {
    return { embeddings: config.embeddings };
  }

  async doWork(ctx: PhaseContext): Promise<PhaseResult> {
    const { config, outputDir, force } = ctx;
    const embConfig = config.embeddings;
    const vectorsPath = join(outputDir, "vectors");
    const tfidfPath = join(outputDir, "tfidf_idf.json");
    const cachePath = join(outputDir, ".cache", "node-texts.json");
    const inputHashPath = join(outputDir, ".cache", "embeddings-input-hash.txt");
    const configHashPath = join(outputDir, ".cache", "embeddings-config-hash.txt");

    if (!embConfig.enabled) {
      removeDirectory(vectorsPath);
      removeFile(tfidfPath);
      removeFile(cachePath);
      removeFile(inputHashPath);
      removeFile(configHashPath);
      return { processed: 0, skipped: true, skipReason: "disabled in config" };
    }

    const currentConfigHash = getEmbeddingsConfigHash(config.embeddings);
    const configChanged = readHashFile(configHashPath) !== currentConfigHash;
    const effectiveForce = force || configChanged || !config.incremental;

    const graphJsonPath = join(outputDir, "graph-enriched.json");
    const graphData = JSON.parse(readFileSync(graphJsonPath, "utf-8")) as GraphData;

    const communitySummaries = loadCommunitySummaries(outputDir);
    const nodeDescriptions = loadNodeDescriptions(outputDir);

    const items = graphData.nodes.map((node) => {
      const communityId = node.community != null ? String(node.community) : undefined;
      const summary = communityId ? communitySummaries.get(communityId) : undefined;
      const description = nodeDescriptions.get(node.id);

      return {
        id: node.id,
        text: composeNodeText(
          {
            id: node.id,
            label: node.label,
            type: node.type,
            signature: node.signature,
            docstring: node.docstring,
            bases: node.bases,
            source_file: node.source_file,
          },
          summary,
          description,
        ),
      };
    });

    const currentTexts = new Map(items.map((item) => [item.id, item.text]));
    const previousTexts = effectiveForce ? new Map<string, string>() : loadNodeTextCache(outputDir);
    const changedIds = effectiveForce
      ? new Set(items.map((item) => item.id))
      : getChangedNodeIds(currentTexts, previousTexts);
    const removedIds = effectiveForce ? new Set<string>() : getRemovedNodeIds(currentTexts, previousTexts);

    const vectorStore = new VectorStore(outputDir);
    await vectorStore.initialize();

    try {
      const existingRecords = effectiveForce ? [] : await vectorStore.loadAllRecords();
      const existingVectors = new Map(existingRecords.map((record) => [record.id, record.vector]));
      const itemsNeedingEmbeddings = effectiveForce
        ? items
        : items.filter((item) => changedIds.has(item.id) || !existingVectors.has(item.id));

      const staleVectorIds = new Set<string>();
      for (const id of existingVectors.keys()) {
        if (!currentTexts.has(id)) staleVectorIds.add(id);
      }

      if (itemsNeedingEmbeddings.length === 0 && removedIds.size === 0 && staleVectorIds.size === 0) {
        atomicWriteJson(getNodeTextCachePath(outputDir), Object.fromEntries(currentTexts));
        return { processed: 0, skipped: true, skipReason: "up to date" };
      }

      if (itemsNeedingEmbeddings.length === 0) {
        if (!embConfig.provider && staleVectorIds.size > 0 && items.length > 0) {
          const result = await generateTfidf(ctx, graphData, items, items, [], currentTexts, vectorStore);
          if (!result.skipped) {
            writeVectorMeta(ctx.outputDir, {
              provider: null,
              models: null,
              dimensions: 384,
              record_count: items.length,
              created_at: new Date().toISOString(),
            });
          }
          return result;
        }

        const cleanedRecords = existingRecords.filter((record) => !staleVectorIds.has(record.id));
        await vectorStore.upsert(cleanedRecords);
        atomicWriteJson(getNodeTextCachePath(outputDir), Object.fromEntries(currentTexts));
        return { processed: 0, skipped: true, skipReason: "stale cleanup only" };
      }

      let result: PhaseResult;
      const embeddingProvider = await ctx.providerRegistry.acquireEmbedding(embConfig.provider);
      if (!embeddingProvider) {
        result = await generateTfidf(ctx, graphData, items, itemsNeedingEmbeddings, existingRecords, currentTexts, vectorStore);
      } else {
        result = await generateWithProvider(ctx, embeddingProvider, graphData, itemsNeedingEmbeddings, existingRecords, currentTexts, vectorStore);
      }

      if (!result.skipped) {
        const providerConfig = embConfig.provider ? ctx.config.providers[embConfig.provider] ?? null : null;
        const dimensions = items[0] ? (await vectorStore.loadAllRecords())[0]?.vector.length ?? 384 : 384;
        const meta: VectorMeta = {
          provider: providerConfig,
          models: providerConfig?.type === "onnx" ? {
            cache_dir: ctx.config.models.cache_dir,
            download_on_first_use: ctx.config.models.download_on_first_use,
          } : null,
          dimensions,
          record_count: items.length,
          created_at: new Date().toISOString(),
        };
        writeVectorMeta(ctx.outputDir, meta);
      }

      return result;
    } finally {
      await vectorStore.dispose();
    }
  }
}

async function generateTfidf(
  ctx: PhaseContext,
  graphData: GraphData,
  allItems: Array<{ id: string; text: string }>,
  itemsToEmbed: Array<{ id: string; text: string }>,
  existingRecords: VectorRecord[],
  currentTexts: Map<string, string>,
  vectorStore: VectorStore,
): Promise<PhaseResult> {
  const engine = new TfidfEmbeddingEngine();
  try {
    log.info(`Generating TF-IDF embeddings (${itemsToEmbed.length} nodes)...`);
    engine.buildVocabulary(allItems.map((item) => item.text));
    const embeddings = itemsToEmbed.length > 0 ? engine.embedBatch(itemsToEmbed) : [];
    await storeEmbeddings({ graphData, embeddings, existingRecords, currentTexts, vectorStore, outputDir: ctx.outputDir, vocabulary: engine.serializeVocabulary() });
    return { processed: embeddings.length, skipped: false };
  } catch (err) {
    const msg = errorMessage(err);
    log.warn(`TF-IDF embeddings failed (non-blocking): ${msg}`);
    return { processed: 0, skipped: true, skipReason: msg };
  } finally {
    engine.dispose();
  }
}

async function generateWithProvider(
  ctx: PhaseContext,
  provider: EmbeddingProvider,
  graphData: GraphData,
  items: Array<{ id: string; text: string }>,
  existingRecords: VectorRecord[],
  currentTexts: Map<string, string>,
  vectorStore: VectorStore,
): Promise<PhaseResult> {
  try {
    const ready = await provider.initialize();
    if (!ready) return { processed: 0, skipped: true, skipReason: "embedding engine unavailable" };

    log.info(`Generating provider embeddings (${items.length} nodes)...`);
    const embeddings = await provider.embedBatch(items);
    await storeEmbeddings({ graphData, embeddings, existingRecords, currentTexts, vectorStore, outputDir: ctx.outputDir });
    return { processed: embeddings.length, skipped: false };
  } catch (err) {
    const msg = errorMessage(err);
    log.warn(`Provider embeddings failed (non-blocking): ${msg}`);
    return { processed: 0, skipped: true, skipReason: msg };
  } finally {
    await provider.dispose();
  }
}

async function storeEmbeddings(options: {
  graphData: GraphData;
  embeddings: Array<{ id: string; text: string; vector: Float32Array }>;
  existingRecords: VectorRecord[];
  currentTexts: Map<string, string>;
  vectorStore: VectorStore;
  outputDir: string;
  vocabulary?: Record<string, number>;
}): Promise<void> {
  const { graphData, embeddings, existingRecords, currentTexts, vectorStore, outputDir, vocabulary } = options;

  const updatedVectors = new Map(embeddings.map((embedding) => [embedding.id, Array.from(embedding.vector)]));
  const existingVectorMap = new Map(existingRecords.map((record) => [record.id, record.vector]));

  const records: VectorRecord[] = graphData.nodes.flatMap((node) => {
    const vector = updatedVectors.get(node.id) ?? existingVectorMap.get(node.id);
    if (!vector) return [];
    return {
      id: node.id,
      label: node.label,
      type: node.type,
      repo: node.repo ?? "",
      source_file: node.source_file ?? "",
      community: String(node.community ?? ""),
      text: currentTexts.get(node.id) ?? "",
      vector,
    };
  });

  await vectorStore.upsert(records);
  atomicWriteJson(getNodeTextCachePath(outputDir), Object.fromEntries(currentTexts));

  if (vocabulary) {
    atomicWriteJson(join(outputDir, "tfidf_idf.json"), vocabulary);
  } else {
    removeFile(join(outputDir, "tfidf_idf.json"));
  }

  log.info(`  ${embeddings.length} embeddings stored`);
}

function loadNodeTextCache(outputDir: string): Map<string, string> {
  const path = getNodeTextCachePath(outputDir);
  const raw = readJsonSafe<Record<string, string>>(path);
  return raw ? new Map(Object.entries(raw)) : new Map();
}

function getNodeTextCachePath(outputDir: string): string {
  return join(outputDir, ".cache", "node-texts.json");
}

function getChangedNodeIds(currentTexts: Map<string, string>, previousTexts: Map<string, string>): Set<string> {
  const changed = new Set<string>();
  for (const [id, text] of currentTexts) {
    if (previousTexts.get(id) !== text) changed.add(id);
  }
  return changed;
}

function getRemovedNodeIds(currentTexts: Map<string, string>, previousTexts: Map<string, string>): Set<string> {
  const removed = new Set<string>();
  for (const id of previousTexts.keys()) {
    if (!currentTexts.has(id)) removed.add(id);
  }
  return removed;
}

function loadCommunitySummaries(outputDir: string): Map<string, string> {
  const path = join(outputDir, "community_summaries.json");
  const summaries = readJsonSafe<Array<{ id: string; summary: string }>>(path);
  return summaries ? new Map(summaries.map((summary) => [String(summary.id), summary.summary])) : new Map();
}

function loadNodeDescriptions(outputDir: string): Map<string, string> {
  const path = join(outputDir, "node_descriptions.json");
  const descriptions = readJsonSafe<Array<{ id: string; description: string }>>(path);
  return descriptions ? new Map(descriptions.map((description) => [description.id, description.description])) : new Map();
}

function getEmbeddingsConfigHash(config: { enabled: boolean; provider?: string; batch_size: number }): string {
  return hashObject({
    enabled: config.enabled,
    provider: config.provider ?? null,
    batch_size: config.batch_size,
  });
}

function removeDirectory(path: string): void {
  if (existsSync(path)) rmSync(path, { recursive: true, force: true });
}

function removeFile(path: string): void {
  if (existsSync(path)) unlinkSync(path);
}

export const embeddingsPhase = new EmbeddingsPhase();
